/** @prettier */

import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Container,
  Box,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useLoaderData, useParams} from 'react-router-dom';
import {Auth} from 'aws-amplify';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import UserAddDialog from './UserAddDialog';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import UserEditDialog from './UserEditDialog';
import UserDeleteDialog from './UserDeleteDialog';
//API import
import {
  getCompositionsSitesBySiteID,
  getSettingSites,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack, limit} from '../../define';

type loaderReturn = {
  loginUserEmail: string; //ログインしているユーザーのメールアドレス
  siteName: string; //サイト名
  usersAuthority: apiIF.resParameters.userAuthority[]; //サイトの権限の情報（ログインしているユーザ以外）
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ログインしているユーザのアドレスを取得
    promises.push(Auth.currentAuthenticatedUser());
    //サイトの権限の情報
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //ユーザーの権限情報取得
    promises.push(
      getSettingSites(params.siteID, {
        'max-result': limit.REGISTRATION_NUM_AUTHORITY_MAX,
      })
    );
    const response = await Promise.all(promises);

    const userInfo = response[0];
    const siteInfo = response[1] as apiIF.res.getCompositionsSitesBySiteID;
    const authorityInfo = response[2] as apiIF.res.getSettingSites;

    if (siteInfo['site-authority'] !== 'admin') {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      loginUserEmail: userInfo.attributes.email,
      siteName: siteInfo['site-name'],
      usersAuthority: authorityInfo['users-authority'].filter(info => {
        return info['e-mail'] !== userInfo.attributes.email;
      }),
    };
  } catch {
    return undefined;
  }
}

/**
 * セキュリティ
 * @returns JSX
 */
const SiteSecurity = () => {
  const trans = useTranslation().t; //翻訳関数
  const siteInfo = useLoaderData() as loaderReturn | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  const [usersAuthority, setUsersAuthority] = useState<
    apiIF.resParameters.userAuthority[] | null
  >(null); //ユーザーの権限情報（ログインしているユーザ以外）
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedUser, setSelectedUser] = useState(''); //選択したユーザのemail
  const [hasAuthInfoChanged, setHasAuthInfoChanged] = useState(false); //サイトの権限の情報の更新フラグ

  //ユーザーの権限情報取得
  useEffect(() => {
    if (siteInfo !== undefined) {
      setUsersAuthority(siteInfo.usersAuthority);
    }
  }, [siteInfo]);

  //ユーザー権限の登録、編集、削除時に情報を再取得する
  useEffect(() => {
    //パスパラメータを取得できない場合
    //サイトの情報を取得できない場合
    if (siteID === undefined || siteInfo === undefined) {
      return;
    }

    //ユーザーの権限情報取得
    getSettingSites(siteID, {
      'max-result': limit.REGISTRATION_NUM_AUTHORITY_MAX,
    }).then(response => {
      setUsersAuthority(
        response['users-authority'].filter(
          info => info['e-mail'] !== siteInfo.loginUserEmail
        )
      );
      setHasAuthInfoChanged(false);
    });
  }, [hasAuthInfoChanged, siteID, siteInfo]);

  /**
   * Editボタン押下時処理
   * @param siteID サイトID
   */
  const openEditDialog = (email: string) => {
    setIsOpenEditDialog(true);
    setSelectedUser(email);
  };
  /**
   * Deleteボタン押下時処理
   */
  const openDeleteDialog = (email: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedUser(email);
  };

  /**
   * 歯車メニューリスト
   * @param siteID サイトID
   * @returns メニュー一覧
   */
  const gearMenuItem = (email: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-securityEdit${email.replace('@', '')}`,
        label: trans('SiteSecurity.gearMenu.edit'),
        onClick: () => openEditDialog(email),
        icon: {
          className: 'siteSecurity__table__row__gearMenuIcon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `SiteSecurity.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        id: `mv-securityDelete${email.replace('@', '')}`,
        label: trans('SiteSecurity.gearMenu.delete'),
        onClick: () => openDeleteDialog(email),
        icon: {
          className: 'siteSecurity__table__row__gearMenuIcon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `SiteSecurity.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  /**
   * 権限を表示するテーブルを作成
   * @param props props
   * @returns JSX
   * - loginUserEmail ログインしているユーザーのメールアドレス
   * - usersAuthority サイトの権限の情報（ログインしているユーザ以外）
   */
  const AuthTableBody = (props: {
    siteID: string;
    loginUserEmail: string;
    usersAuthority: apiIF.resParameters.userAuthority[];
  }) => (
    <TableBody>
      {/* 一列目はログインしているユーザ */}
      <TableRow className="siteSecurity__table__row--loginUser">
        <TableCell component="th" scope="row">
          {props.loginUserEmail}
        </TableCell>
        <TableCell align="center">
          {trans('SiteSecurity.item.value.securityLevel.admin')}
        </TableCell>
        <TableCell align="center"></TableCell>
      </TableRow>
      {props.usersAuthority.map(userInfo => (
        <TableRow
          key={userInfo['e-mail']}
          className="siteSecurity__table__row--otherUser">
          <TableCell component="th" scope="row">
            {userInfo['e-mail']}
          </TableCell>
          <TableCell align="center">
            {`${trans(
              `SiteSecurity.item.value.securityLevel.${userInfo['site-authority']}`
            )}`}
          </TableCell>
          <TableCell align="center">
            {/* 歯車ボタン */}
            <GearMenu
              id={`mv-btnSecuritySettingOpen${userInfo['e-mail'].replace(
                '@',
                ''
              )}`}
              menuItem={gearMenuItem(userInfo['e-mail'])}
            />
            {userInfo['e-mail'] === selectedUser ? (
              <>
                <UserEditDialog
                  authorityIDInfo={userInfo}
                  setAuthInfoChange={setHasAuthInfoChanged}
                  isOpenDialog={isOpenEditDialog}
                  setIsOpenDialog={setIsOpenEditDialog}
                />
                <UserDeleteDialog
                  authorityIDInfo={userInfo}
                  setAuthInfoChange={setHasAuthInfoChanged}
                  isOpenDialog={isOpenDeleteDialog}
                  setIsOpenDialog={setIsOpenDeleteDialog}
                />
              </>
            ) : (
              <></>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  //パスパラメータを取得できない場合
  //サイトの情報を取得できない場合
  if (siteID === undefined || siteInfo === undefined) {
    return <></>;
  }

  return (
    <Container fixed>
      <Grid container spacing={3}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <Stack>
            <Box>
              <h1>{trans('SiteSecurity.title')}</h1>
            </Box>
            <Box>
              <h3 className="siteSecurity__siteUser">
                {trans('SiteSecurity.instruction')}
              </h3>
            </Box>
          </Stack>
        </Grid>
        {/** 一覧 */}
        <Grid item xs={12} container spacing={2}>
          {/** サイトの名前 */}
          <Grid item xs className="siteSecurity__siteName">
            {siteInfo.siteName}
          </Grid>
          {/** 追加ボタン */}
          <Grid item xs="auto">
            <UserAddDialog setAuthInfoChange={setHasAuthInfoChanged} />
          </Grid>
          {/** 権限情報取得前はテーブルを表示させない */}
          {usersAuthority === null ? (
            <></>
          ) : (
            <Grid item xs={12}>
              {/* 権限テーブル */}
              <TableContainer component={Paper} className="siteSecurity__table">
                <Table aria-label="authority table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {trans('SiteSecurity.item.title.email')}
                      </TableCell>
                      <TableCell align="center">
                        {trans('SiteSecurity.item.title.securityLevel')}
                      </TableCell>
                      <TableCell align="center">
                        {trans('SiteSecurity.item.title.setting')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <AuthTableBody
                    siteID={siteID}
                    loginUserEmail={siteInfo.loginUserEmail}
                    usersAuthority={usersAuthority}
                  />
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};
export default SiteSecurity;
